import {StatusCodes} from "http-status-codes";
import LoadService from "./LoadService";
import ToastService from "./ToastService";
import {getUser} from "./KeycloakService";
import {gatewayClaimHeaders} from "./gatewayClaims";
import {eventBus} from "../events/eventBus";
import i18next from "i18next";

// service qui compte le nombre d appel WS en cours et affiche ou non le loader global
let requestCount = 0;
const loadCounter = {
  addOne: function () {
    requestCount++;
    this.apply();
  },
  removeOne: function () {
    requestCount--;
    this.apply();
  },
  apply: function () {
    if (requestCount > 0) {
      LoadService.open();
    } else {
      LoadService.close();
    }
  },
};

// Classe d'erreur personnalisée pour transporter la réponse et le body
export class AuthenticatedApiError extends Error {
  response: Response;
  body: any;
  constructor(response: Response, body: any) {
    super("Authenticated API error");
    this.response = response;
    this.body = body;
  }
}

const getErrorType = (body: any) => {
  return body?.error;
};

// Statuts pour lesquels `notifyStatusError` prévient déjà l'utilisateur (toast, ou demande
// d'acceptation des conditions sur un 403) : à garder aligné sur son `switch`.
const notifiedStatuses: number[] = [
  StatusCodes.FORBIDDEN,
  StatusCodes.TOO_MANY_REQUESTS,
  StatusCodes.INTERNAL_SERVER_ERROR,
];

// Permet à un appelant d'afficher son propre message d'échec sans doubler celui posé ici. Une
// erreur réseau ou une réponse invalide n'est pas un `AuthenticatedApiError` : rien n'a été affiché.
export const isErrorAlreadyNotified = (error: unknown): boolean =>
  error instanceof AuthenticatedApiError && notifiedStatuses.includes(error.response.status);

// Message d'échec à afficher pour un appel : celui de l'API quand elle en donne un (violations
// de validation d'un 422, sinon `detail`), le message de repli sinon. `null` quand l'échec a
// déjà été signalé par `notifyStatusError`, pour ne pas doubler le toast.
export const getErrorMessage = (error: unknown, fallback: string): string | null => {
  if (isErrorAlreadyNotified(error)) {
    return null;
  }
  if (!(error instanceof AuthenticatedApiError)) {
    return fallback;
  }

  const violations = error.body?.violations;
  if (Array.isArray(violations)) {
    // Une violation peut ne porter que son `propertyPath` : on ne garde que les messages exploitables
    const messages = violations
      .map((violation: {message?: unknown}) => violation?.message)
      .filter((message): message is string => typeof message === "string" && message !== "");
    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  const detail = error.body?.detail;
  return typeof detail === "string" && detail !== "" ? detail : fallback;
};

const notifyStatusError = async (response: Response, body: any) => {
  switch (response.status) {
    case StatusCodes.FORBIDDEN:
      if (getErrorType(body) === "terms_not_accepted") {
        eventBus.emit("privacyApprovalRequired");
      } else {
        ToastService.error(i18next.t("api.forbidden"));
      }
      break;
    case StatusCodes.TOO_MANY_REQUESTS:
      ToastService.error(i18next.t("api.tooManyRequests"));
      break;
    case StatusCodes.INTERNAL_SERVER_ERROR:
      ToastService.error(i18next.t("api.serverError"));
      break;
    default:
      eventBus.emit("privacyApprovalNotRequired");
  }
};

const request = async (
  url: string,
  method: string,
  headers: HeadersInit,
  body: object | null,
  isFormData: boolean = false,
  globalLoader: boolean = true,
): Promise<Response> => {
  if (globalLoader) {
    loadCounter.addOne();
  }
  const user = getUser();
  let response: Response;
  try {
    response = await fetch(url, {
      method: method,
      headers: {
        Accept: "application/json",
        ...headers,
        ...gatewayClaimHeaders(url, user),
        Authorization: "Bearer " + user?.access_token,
        OkwindClient: "APP_ADMIN_CLIENT",
      },
      body: body && !isFormData ? JSON.stringify(body) : (body as FormData),
    });
  } finally {
    if (globalLoader) {
      loadCounter.removeOne();
    }
  }

  let responseBody: any;
  try {
    responseBody = await response.clone().json();
  } catch {
    responseBody = null;
  }
  await notifyStatusError(response, responseBody);

  if (!response.ok) {
    throw new AuthenticatedApiError(response, responseBody);
  }
  return response;
};

const authenticatedApi = {
  post: async function (url: string, body: object | null, globalLoader: boolean = true): Promise<Response> {
    return await request(
      url,
      "POST",
      {
        "Content-Type": "application/ld+json",
      },
      body,
      false,
      globalLoader,
    );
  },

  postFormData: async function (url: string, body: FormData, globalLoader: boolean = true): Promise<Response> {
    return request(url, "POST", {}, body, true, globalLoader);
  },

  patch: async function (url: string, body: object | null, globalLoader: boolean = true): Promise<Response> {
    return await request(
      url,
      "PATCH",
      {
        "Content-Type": "application/ld+json",
      },
      body,
      false,
      globalLoader,
    );
  },

  get: async function (
    url: string,
    globalLoader: boolean = true,
    accept: string = "application/json",
  ): Promise<Response> {
    return await request(
      url,
      "GET",
      {
        "Content-Type": "application/json",
        Accept: accept,
      },
      null,
      false,
      globalLoader,
    );
  },

  getImage: async function (url: string): Promise<string> {
    const user = getUser();
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + user?.access_token,
        OkwindClient: "APP_ADMIN_CLIENT",
      },
    });
    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  getAuthorizationHeaders: function () {
    const user = getUser();
    return {
      Authorization: "Bearer " + user?.access_token,
      "X-Requested-With": null,
      OkwindClient: "APP_ADMIN_CLIENT",
    };
  },
};

export default authenticatedApi;
